// bluaIQ — client_question client UI v1 — PRESENTATION.
// Sealed contract name: `insulation-lane-checkpoint-v1`.
// Spec: docs/SPEC_CLIENT_QUESTION_UI_V1.md (committed, Codex-sealed).
//
// READ-ONLY, PRESENTATION ONLY. No fetch, no Prisma, no network, no write. The
// card renders the sealed NINE fields per the ratified mockup (a–i). Everything
// the client must not see is excluded STRUCTURALLY, not by CSS:
//   · `raw_text` and any raw engine evidence key cannot arrive — the evidence
//     projection allowlist (assertClientEvidenceRecord) rejects any record that
//     is not EXACTLY the 4 curated keys (spec §3).
//   · `source_user_truth` is NEVER rendered verbatim — the client sees a fixed
//     owner-criteria phrase; the auditor keeps the reference through its own
//     channel (spec §2.0.1 / §5.2 #8).
//   · `linked_blocking_reason`'s raw prose (which can carry engine refs like
//     "(UT-7)") is NEVER rendered — the block-link (f) is chrome gated on the
//     field's PRESENCE (spec §2.-1).
//   · `question_id` / `answer_slot` are engine slugs and never reach the DOM.
//
// Question CONTENT (question_text, options[].label, suggested_interpretation)
// arrives {es,en} from the engine and is rendered verbatim, never rewritten.

import { useMemo } from 'react'
import {
  assertClientQuestion,
  type ClientQuestion,
  type ClientQuestionEvidence,
} from './takeoffClientQuestion'
import {
  clientQuestionCopy,
  clientQuestionEmptyState,
  clientQuestionPendingBadge,
  clientQuestionSourcePhrase,
} from './takeoffClientCopy'

const ACCENT = '#00BCD4'

/** Evidence box (e). The ≤160 quote renders in monospace, wrapping, NEVER
 *  scrolling horizontally (spec §4.2). The sheet+page reference line is its own
 *  line beneath the quote and is OMITTED ENTIRELY when both are null — never
 *  "null", never a dash that reads as a value (spec §2.-0). */
function EvidenceBox({ record, isEs }: { record: ClientQuestionEvidence; isEs: boolean }) {
  const hasRef = record.sheet !== null || record.page !== null
  const refParts: string[] = []
  if (record.sheet !== null) refParts.push(record.sheet)
  if (record.page !== null) refParts.push(`${isEs ? 'p.' : 'p.'} ${record.page}`)
  return (
    <div
      data-testid="client-question-evidence"
      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-w-0"
    >
      <p
        data-testid="client-question-evidence-quote"
        className="font-mono text-[12px] text-slate-700 m-0"
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {record.evidence_quote}
      </p>
      {hasRef && (
        <p data-testid="client-question-evidence-ref" className="text-[11px] text-slate-500 mt-1 mb-0">
          {refParts.join(' · ')}
        </p>
      )}
    </div>
  )
}

export interface TakeoffClientQuestionCardProps {
  /** Raw checkpoint client_question — validated here; a violating question
   *  renders a neutral "unavailable" marker and logs internally (same posture as
   *  the shell's contract-invalid branch), never leaking the violating content. */
  question: unknown
  isEs: boolean
  /** Optional client-SAFE surface label for the (a) "item · surface" chip. Not
   *  derived from any engine slug — rendered only when a safe value is provided
   *  by the caller. Omitted in v1 unless plumbed. */
  surfaceLabel?: string
  /** Answered badge. Only true AFTER an answer is saved (Codex #467); no write
   *  path exists in v1, so this stays false and the card shows "pending". */
  answered?: boolean
}

export default function TakeoffClientQuestionCard({
  question,
  isEs,
  surfaceLabel,
  answered = false,
}: TakeoffClientQuestionCardProps) {
  const validated = useMemo<ClientQuestion | null>(() => {
    try {
      return assertClientQuestion(question)
    } catch (err) {
      // Internal-only log — no network, no remote logging (shell precedent).
      // eslint-disable-next-line no-console
      console.error('[TakeoffClientQuestionCard] client_question failed contract validation:', err)
      return null
    }
  }, [question])

  if (!validated) {
    return (
      <div
        data-testid="client-question-unavailable"
        className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-500"
      >
        {isEs ? 'Pregunta no disponible' : 'Question not available'}
      </div>
    )
  }

  const q = validated
  const text = isEs ? q.question_text.es : q.question_text.en

  return (
    <div
      data-testid="client-question-card"
      className="rounded-xl border border-slate-200 bg-white min-w-0"
      style={{ padding: 'clamp(1rem, 4vw, 1.25rem)' }}
    >
      {/* (a) status badge + optional item·surface (stacks on narrow screens) */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span
          data-testid="client-question-badge"
          className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-[800]"
          style={
            answered
              ? { background: '#dcfce7', color: '#166534' }
              : { background: '#fef3c7', color: '#92400e' }
          }
        >
          {answered ? clientQuestionCopy('badgeAnswered', isEs) : clientQuestionCopy('badgePending', isEs)}
        </span>
        {surfaceLabel && (
          <span data-testid="client-question-surface" className="text-[11px] text-slate-500">
            {surfaceLabel}
          </span>
        )}
      </div>

      {/* (b) question text — 16px */}
      <p data-testid="client-question-text" className="text-[16px] font-[700] text-slate-800 m-0" style={{ wordBreak: 'break-word' }}>
        {text}
      </p>

      {/* (c) honest subtitle — chrome, says why it is being asked */}
      <p data-testid="client-question-subtitle" className="text-[13px] text-slate-500 mt-1 mb-3" style={{ wordBreak: 'break-word' }}>
        {clientQuestionCopy('honestSubtitle', isEs)}
      </p>

      {/* (d) options — NON-INTERACTIVE rows (Codex #467 correction 1). This is a
          read-only module: there is no write path to save an answer, so an
          enabled button would be a control that silently does nothing. The
          options are DISPLAYED (each ≥44px, wrap to 2 lines) so the client can
          read the choices, but they are not actionable until a separately-gated
          write flow lands. No aria-pressed / no selected state — the
          recommendation, when present, is shown apart (below), never here. */}
      <ul data-testid="client-question-options" className="flex flex-col gap-2 list-none p-0 m-0" aria-label={isEs ? 'Opciones' : 'Options'}>
        {q.options.map((opt) => (
          <li
            key={opt.value}
            data-testid="client-question-option"
            data-option-value={opt.value}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-700"
            style={{
              minHeight: 44,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
            title={isEs ? opt.label.es : opt.label.en}
          >
            {isEs ? opt.label.es : opt.label.en}
          </li>
        ))}
      </ul>

      {/* (h) "noted" and (g)/§4.1 the reversibility line are POST-SAVE ONLY
          (Codex #467 correction 1): they may appear only AFTER an answer is
          successfully saved, and this read-only module has no write path — so
          neither is rendered in v1. The strings stay in takeoffClientCopy.ts for
          when the write flow lands. */}

      {/* suggested_interpretation — rendered but NEVER preselected (field 6) */}
      {q.suggested_interpretation && (
        <div
          data-testid="client-question-suggestion"
          className="rounded-lg px-3 py-2 mt-3"
          style={{ background: 'rgba(0,188,212,0.08)', border: `1px solid ${ACCENT}` }}
        >
          <p className="text-[11px] font-[800] m-0" style={{ color: ACCENT }}>
            {clientQuestionCopy('suggestionLabel', isEs)}
          </p>
          <p className="text-[13px] text-slate-700 mt-0.5 mb-0" style={{ wordBreak: 'break-word' }}>
            {isEs ? q.suggested_interpretation.es : q.suggested_interpretation.en}
          </p>
          {/* provenance — the fixed owner-criteria phrase, NEVER the raw
              source_user_truth (spec §5.2 #8). */}
          <p data-testid="client-question-source-phrase" className="text-[11px] text-slate-400 mt-0.5 mb-0">
            {clientQuestionSourcePhrase(isEs)}
          </p>
        </div>
      )}

      {/* (e) evidence box(es) — curated quotes only */}
      {q.attached_evidence_records.length > 0 && (
        <div className="flex flex-col gap-2 mt-3">
          <p className="text-[11px] font-[700] text-slate-500 m-0">{clientQuestionCopy('evidenceLead', isEs)}</p>
          {q.attached_evidence_records.map((rec) => (
            <EvidenceBox key={rec.evidence_id} record={rec} isEs={isEs} />
          ))}
        </div>
      )}

      {/* (f) block link — from field 9's PRESENCE, never its raw prose */}
      {q.linked_blocking_reason.trim().length > 0 && (
        <p data-testid="client-question-block-link" className="text-[12px] text-amber-700 mt-3 mb-0" style={{ wordBreak: 'break-word' }}>
          {clientQuestionCopy('blockLink', isEs)}
        </p>
      )}
    </div>
  )
}

/** The card list + (i) project summary badge + empty state. Reads the already
 *  validated/derived questions; when zero, shows the honest empty state and
 *  ZERO cards — spec §6, never a prose parser. */
export function TakeoffClientQuestionList({
  questions,
  isEs,
}: {
  questions: unknown[]
  isEs: boolean
}) {
  const pending = questions.length
  if (pending === 0) {
    return (
      <div data-testid="client-question-empty" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-500">
        {clientQuestionEmptyState(isEs)}
      </div>
    )
  }
  return (
    <section data-testid="client-question-list" className="flex flex-col gap-3 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-[800] text-slate-700 m-0">{clientQuestionCopy('sectionTitle', isEs)}</h3>
        <span
          data-testid="client-question-pending-badge"
          className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-[800]"
          style={{ background: '#fef3c7', color: '#92400e' }}
        >
          {clientQuestionPendingBadge(pending, isEs)}
        </span>
      </div>
      {questions.map((question, i) => (
        <TakeoffClientQuestionCard key={i} question={question} isEs={isEs} />
      ))}
    </section>
  )
}

/** AUDITOR CHANNEL (Codex #467 correction 3). The client card hides the engine
 *  references (`source_user_truth`, raw `linked_blocking_reason`, `question_id`,
 *  `answer_slot`); the auditor must SEE them, verbatim, through its own surface.
 *  This is that surface — deliberately raw and technical: engine slugs are
 *  EXPECTED here, which is exactly why it is auditor-only and never the client
 *  view. (`raw_text` is not shown because it is never in the website payload —
 *  it lives only in the engine's out-of-band artifact, spec §3.1.) Resilient
 *  per-item: a malformed question shows its raw JSON, never breaks the table. */
export function TakeoffClientQuestionAuditorTable({
  questions,
  isEs,
}: {
  questions: unknown[]
  isEs: boolean
}) {
  if (questions.length === 0) {
    return (
      <div data-testid="client-question-auditor-empty" className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
        {isEs ? 'Canal de auditor: sin preguntas estructuradas.' : 'Auditor channel: no structured questions.'}
      </div>
    )
  }
  return (
    <section data-testid="client-question-auditor" className="flex flex-col gap-3 min-w-0">
      <h3 className="text-[13px] font-[800] text-slate-700 m-0">
        {isEs ? 'Canal de auditor — client_question (crudo)' : 'Auditor channel — client_question (raw)'}
      </h3>
      {questions.map((question, i) => {
        let q: ClientQuestion | null = null
        try {
          q = assertClientQuestion(question)
        } catch {
          q = null
        }
        if (!q) {
          return (
            <pre
              key={i}
              data-testid="client-question-auditor-malformed"
              className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-800 overflow-x-auto"
            >
              {JSON.stringify(question, null, 2)}
            </pre>
          )
        }
        return (
          <div
            key={q.question_id}
            data-testid="client-question-auditor-row"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[12px] text-slate-700 min-w-0"
          >
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 m-0" style={{ wordBreak: 'break-word' }}>
              <dt className="font-[700] text-slate-500">question_id</dt>
              <dd className="m-0 font-mono">{q.question_id}</dd>
              <dt className="font-[700] text-slate-500">answer_slot</dt>
              <dd className="m-0 font-mono">{q.answer_slot}</dd>
              <dt className="font-[700] text-slate-500">source_user_truth</dt>
              <dd data-testid="client-question-auditor-source-user-truth" className="m-0 font-mono">{q.source_user_truth}</dd>
              <dt className="font-[700] text-slate-500">linked_blocking_reason</dt>
              <dd data-testid="client-question-auditor-blocking-reason" className="m-0">{q.linked_blocking_reason}</dd>
              <dt className="font-[700] text-slate-500">options</dt>
              <dd className="m-0 font-mono">{q.options.map((o) => o.value).join(' · ')}</dd>
              <dt className="font-[700] text-slate-500">evidence</dt>
              <dd className="m-0">
                {q.attached_evidence_records.map((r) => (
                  <div key={r.evidence_id} className="font-mono">
                    {r.evidence_id}
                    {r.sheet !== null ? ` · ${r.sheet}` : ''}
                    {r.page !== null ? ` · p.${r.page}` : ''} — {r.evidence_quote}
                  </div>
                ))}
              </dd>
            </dl>
          </div>
        )
      })}
    </section>
  )
}
